/**
 * Paleta do design system — "Core Enterprise Logic".
 *
 * Fonte da verdade: `base-html/core_enterprise_logic_2/DESIGN.md` e o
 * `tailwind.config.js` de `apps/web`, que já era uma porta fiel dele.
 *
 * Antes deste arquivo a paleta era outra ("Obsidian Aurora": primary `#1a73e8`,
 * secondary violeta, sidebar `#1a1f2e` escura) e era ela que os 22 componentes
 * usavam — todos derivam cor daqui e emitem `style` inline, nenhum lê as classes
 * utilitárias do Tailwind. O efeito era markup correto em volta de componentes
 * com a paleta errada em 84 telas.
 *
 * As CHAVES foram preservadas exatamente, de propósito: retargetar os valores
 * converge os 22 componentes de uma vez, sem reescrever 4224 linhas de markup.
 * A migração para classes de token continua valendo, mas é independente disto.
 *
 * Mapeamento para os papéis Material 3 usados no protótipo:
 *   primary.500        = primary            #002045
 *   primary.50/100     = primary-fixed / -dim
 *   neutral.*          = rampa de surface + outline + on-surface
 *   surface.sidebar    = surface-container-lowest (era escura)
 *   text.primary       = on-surface          #191c1e
 *   border.default     = outline-variant     #c4c6cf
 */

export const colors = {
  // Brand — Primary (Enterprise Blue)
  primary: {
    50: '#d6e3ff', // primary-fixed
    100: '#adc7f7', // primary-fixed-dim / inverse-primary
    200: '#86a0cd', // on-primary-container
    300: '#455f88', // surface-tint
    400: '#2d476f', // on-primary-fixed-variant
    500: '#002045', // primary
    600: '#001b3c', // on-primary-fixed
    700: '#001631',
    800: '#001126',
    900: '#000b1a',
    DEFAULT: '#002045',
  },

  // Brand — Secondary (cinza-aço M3, para ícones e metadados)
  secondary: {
    50: '#d3e4fe', // secondary-fixed
    100: '#d0e1fb', // secondary-container
    200: '#b7c8e1', // secondary-fixed-dim
    300: '#8b9bb3',
    400: '#6b7a90',
    500: '#505f76', // secondary
    600: '#54647a', // on-secondary-container
    700: '#38485d', // on-secondary-fixed-variant
    800: '#243549',
    900: '#0b1c30', // on-secondary-fixed
    DEFAULT: '#505f76',
  },

  success: {
    50: '#d3f5d6', // success-container
    100: '#b6ebbb',
    200: '#8dd996',
    300: '#63c470',
    400: '#43a852',
    500: '#2e7d32', // success-green
    600: '#256628',
    700: '#1b4f1e',
    800: '#113d15',
    900: '#0b3d11', // on-success-container
    DEFAULT: '#2e7d32',
  },

  warning: {
    50: '#ffddba', // tertiary-fixed
    100: '#f2bc82', // tertiary-fixed-dim
    200: '#e8a55e',
    300: '#dd8f3c',
    400: '#f57c00',
    500: '#ed6c02', // warning-amber
    600: '#c25a02',
    700: '#8a4a10',
    800: '#633f0f', // on-tertiary-fixed-variant
    900: '#2b1700', // on-tertiary-fixed
    DEFAULT: '#ed6c02',
  },

  error: {
    50: '#ffdad6', // error-container
    100: '#ffb4ab',
    200: '#ff897d',
    300: '#ff5449',
    400: '#de3730',
    500: '#ba1a1a', // error
    600: '#a80710',
    700: '#93000a', // on-error-container
    800: '#7a0007',
    900: '#410002',
    DEFAULT: '#ba1a1a',
  },

  info: {
    50: '#d6e3ff', // processing-container
    100: '#adc7f7',
    200: '#86a0cd',
    300: '#4b6a9b',
    400: '#2d476f',
    500: '#1a365d', // enterprise-blue / primary-container
    600: '#12294a',
    700: '#001b3c', // on-processing-container
    800: '#001531',
    900: '#000f24',
    DEFAULT: '#1a365d',
  },

  // Rampa neutra = superfícies M3 + outline + on-surface
  neutral: {
    50: '#ffffff', // surface-container-lowest
    100: '#f7f9fb', // surface / background
    200: '#f2f4f6', // surface-container-low
    300: '#eceef0', // surface-container
    400: '#e6e8ea', // surface-container-high
    500: '#e0e3e5', // surface-container-highest / surface-variant
    600: '#c4c6cf', // outline-variant
    700: '#74777f', // outline
    800: '#43474e', // on-surface-variant
    900: '#191c1e', // on-surface
    DEFAULT: '#43474e',
  },

  // Superfícies
  surface: {
    page: '#f7f9fb',
    card: '#ffffff',
    elevated: '#ffffff',
    header: '#ffffff',
    // Era '#1a1f2e' (rail escuro). O DESIGN.md pede ambiente claro e profissional.
    sidebar: '#ffffff',
    sidebarHover: '#eceef0',
    sidebarActive: '#d6e3ff',
    input: '#ffffff',
    inputDisabled: '#f2f4f6',
    overlay: 'rgba(0, 16, 36, 0.45)',
    tooltip: '#2d3133', // inverse-surface
  },

  // Sobre fundo escuro (tooltip, toast inverso)
  inverse: {
    text: '#eff1f3', // inverse-on-surface
    textSecondary: '#c4c6cf',
    textMuted: '#8e9099',
    surface: '#2d3133', // inverse-surface
    border: 'rgba(255, 255, 255, 0.12)',
  },

  // Texto
  text: {
    primary: '#191c1e', // on-surface
    secondary: '#43474e', // on-surface-variant
    muted: '#74777f', // outline — só para desabilitado
    inverse: '#ffffff', // on-primary
    inverseSecondary: '#c4c6cf',
    link: '#002045', // primary
    error: '#ba1a1a', // error
  },

  // Bordas
  border: {
    light: '#e0e3e5',
    default: '#c4c6cf', // outline-variant
    focus: '#002045',
    error: '#ba1a1a',
    disabled: '#eceef0',
  },

  // Anel de foco
  focus: {
    ring: '#002045',
    ringError: '#ba1a1a',
    width: '2px',
    offset: '2px',
  },
} as const;

export type ColorTokens = typeof colors;
